import { SxProps, Theme } from '@mui/material';
import { characterStyles, colors, radii } from '../theme/tokens';

// XD field styles, shared by the redesigned screens.
//
// Two contexts:
//   - `FIELD_SX`: fields over the light background (the filter rows of the Home
//     and of Liste users). White background, grey border, text entered in blue
//     (`.saisie-champ`) and grey placeholder (`.info-champ`).
//   - `ON_PRIMARY_FIELD_SX` / `ON_PRIMARY_LABEL_SX`: fields over the blue
//     (Login and the XD modals). Same white field, no border, with the
//     focus and errors in light orange, which is what reads over blue;
//     the label in white.
//
// Each `sx` applies to TextField (the selector with a space), to Select (whose
// root is already the OutlinedInput, the selector with `&.`) and to the
// DatePicker's inner TextField.

export const FIELD_SX: SxProps<Theme> = {
  '&.MuiOutlinedInput-root, & .MuiOutlinedInput-root': {
    backgroundColor: colors.white,
    borderRadius: `${radii.card}px`,
    color: characterStyles.fieldInput.color,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.border,
  },
  '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
    {
      borderColor: colors.accentLight,
    },
  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
    {
      borderColor: colors.primary,
      borderWidth: 1,
    },
  '& .MuiInputBase-input::placeholder': {
    color: characterStyles.fieldHint.color,
    opacity: 1,
  },
  '& .MuiInputLabel-root': {
    color: colors.muted,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: colors.primary,
  },
  '& .MuiSvgIcon-root': {
    color: colors.accent,
  },
};

export const ON_PRIMARY_FIELD_SX: SxProps<Theme> = {
  '&.MuiOutlinedInput-root, & .MuiOutlinedInput-root': {
    backgroundColor: colors.white,
    borderRadius: `${radii.card}px`,
    color: characterStyles.fieldInput.color,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent',
  },
  '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
    {
      borderColor: colors.accentLight,
    },
  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
    {
      borderColor: colors.accentLight,
      borderWidth: 2,
    },
  '&.MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':
    {
      borderColor: colors.accentLight,
    },
  '& .MuiInputBase-input::placeholder': {
    color: characterStyles.fieldHint.color,
    opacity: 1,
  },
  // Disabled (during a submission, or the read-only user record):
  // the value is still read in the blue of the entered text, dimmed; MUI's
  // default grey made it almost invisible over the white field.
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: characterStyles.fieldInput.color,
    opacity: 0.75,
  },
  '&.MuiOutlinedInput-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled':
    {
      backgroundColor: 'rgba(255, 255, 255, 0.85)',
    },
  '& .MuiSvgIcon-root': {
    color: colors.accent,
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
    color: colors.accentLight,
    '&.Mui-error': { color: colors.accentLight },
  },
};

// XD modal fields that are NOT the main text input (dropdowns, dates): no
// fill, a white outline, white text. Same focus and error colours as the
// filled variant.
export const ON_PRIMARY_OUTLINE_FIELD_SX: SxProps<Theme> = {
  ...(ON_PRIMARY_FIELD_SX as object),
  '&.MuiOutlinedInput-root, & .MuiOutlinedInput-root': {
    backgroundColor: 'transparent',
    borderRadius: `${radii.card}px`,
    color: colors.white,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: colors.white,
  },
  '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
    {
      borderColor: colors.accentLight,
    },
  '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
    {
      borderColor: colors.accentLight,
      borderWidth: 2,
    },
  '& .MuiInputBase-input::placeholder': {
    color: colors.white,
    opacity: 0.7,
  },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: colors.white,
    opacity: 0.75,
  },
  '&.MuiOutlinedInput-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled':
    {
      backgroundColor: 'transparent',
    },
};

// Row label over a light background (the white card of public verification):
// base text, not the XD secondary grey, which over white does not reach the
// minimum contrast (DESIGN.md, "Accessibility finding").
export const ON_LIGHT_LABEL_SX = {
  color: colors.textBase,
  fontSize: '0.875rem',
  lineHeight: 1.5,
} as const;

export const ON_PRIMARY_LABEL_SX = {
  color: colors.white,
  fontSize: '0.875rem',
  lineHeight: 1.5,
  '&.Mui-focused': { color: colors.white },
  '&.Mui-error': { color: colors.white },
} as const;

// No design in the XD for validation errors over blue (gap 8):
// the same light orange that Login uses for its messages, because MUI's
// default red does not read over the blue.
export const ON_PRIMARY_ERROR_SX = {
  color: colors.accentLight,
  fontSize: '0.75rem',
  lineHeight: 1.5,
  mt: 0.5,
} as const;
